import React from 'react';
import { useTranslation } from 'react-i18next';
import ProfileAvatar from '../common/ProfileAvatar';
import RatingStars from '../common/RatingStars';
import { Contact } from '../../types/contact';

interface RecruiterProfileModalContentProps {
  contact: Contact;
  isRecruiter?: boolean;
  isFreelancer?: boolean;
  allianceCount?: number;
  getFreelancerSubtitle?: (contact: Contact, t: unknown) => React.ReactNode;
  className?: string;
}

const RecruiterProfileModalContent: React.FC<RecruiterProfileModalContentProps> = ({
  contact,
  isRecruiter = false,
  isFreelancer = false,
  allianceCount = 0,
  getFreelancerSubtitle,
  className = '',
}) => {
  const { t } = useTranslation();

  const userRating = contact.stats ? contact.stats?.overall_rating : contact.overall_rating;

  return (
    <div className={`hidden md:flex items-center gap-4 ${className}`}>
      <ProfileAvatar
        profile_id={contact.id}
        first_name={contact.first_name ?? ''}
        avatar_url={contact.avatar_url}
        className="w-32 h-32 rounded-full aspect-square"
      />

      <div className="flex flex-row gap-6 items-start justify-between text-base text-secondary w-full">
        <div>
          <h2 className="text-lg font-bold text-secondary">
            {contact.first_name}, <span className="text-sm text-gray-500">{contact.job_title}</span>
          </h2>
          <span className="text-sm text-gray-500 flex">{contact.company_name}</span>
        </div>

        <div className="flex flex-row items-start text-base text-secondary gap-4 w-full">
          <div className="w-1/4 break-words">
            <h5 className="text-[0.7rem] font-bold text-gray-500">
              {isRecruiter
                ? t('businessServices', { ns: 'freelancers', defaultValue: 'Services offerts' })
                : t('businessIndustries', { defaultValue: "Secteurs d'activité" })}
            </h5>
            <p className="text-[0.6rem] flex flex-wrap gap-1 mt-1">
              {isRecruiter && getFreelancerSubtitle
                ? getFreelancerSubtitle(contact, t)
                : contact.industries?.map(ind => <span key={ind.id}>{ind.label}</span>)}
            </p>
          </div>

          {isFreelancer && (
            <div className="w-1/4 break-words">
              <h5 className="text-[0.7rem] font-bold text-gray-500">
                {t('location', { defaultValue: 'Localisation' })}
              </h5>
              <p className="text-[0.6rem] mt-1">{contact?.cityname}</p>
            </div>
          )}

          <div className="w-1/4 break-words">
            <h5 className="text-[0.7rem] font-bold text-gray-500">
              {t('freelancers:distance', { defaultValue: 'Distance' })}
            </h5>
            <p className="text-[0.6rem] mt-1">0km</p>
          </div>

          <div className="w-1/4 break-words">
            <h5 className="text-[0.7rem] font-bold text-gray-500">
              {t('availability', { defaultValue: 'Évaluation' })}
            </h5>
            <div>
              <RatingStars rating={userRating ?? 0} />
            </div>
          </div>

          {isRecruiter && (
            <div className="w-1/4 break-words">
              <h5 className="text-[0.7rem] font-bold text-gray-500">
                {t('alliances', { ns: 'freelancers' })}
              </h5>
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1">
                <span className="text-white text-xs">{allianceCount}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruiterProfileModalContent;
